import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Hls from 'hls.js';
import { motion, AnimatePresence } from 'framer-motion';

interface FloorVideoPlayerProps {
    hlsSrc?: string;
    fallbackSrc?: string;
    onComplete: () => void;
}

const FLOOR_VIDEO_ASSET_VERSION = '20260517-hq1';

function withVideoVersion(src?: string) {
    if (!src) return undefined;
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}v=${FLOOR_VIDEO_ASSET_VERSION}`;
}

export const FloorVideoPlayer: React.FC<FloorVideoPlayerProps> = ({ hlsSrc, fallbackSrc, onComplete }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isVisible, setIsVisible] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const completedRef = useRef(false);
    const onCompleteRef = useRef(onComplete);
    const loadingRef = useRef(true);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        loadingRef.current = isLoading;
    }, [isLoading]);

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            completedRef.current = false;
            setIsVisible(true);
            setIsLoading(true);
            let hls: Hls | null = null;
            let attemptedHlsFallback = false;
            const hlsSource = withVideoVersion(hlsSrc);
            const highQualitySource = withVideoVersion(fallbackSrc);
            const canNativeHls = !!video.canPlayType('application/vnd.apple.mpegurl');

            const triggerComplete = () => {
                if (completedRef.current) return;
                completedRef.current = true;
                video.pause();
                setIsVisible(false);
                setTimeout(() => onCompleteRef.current(), 800);
            };

            const safePlay = () => {
                video.play().catch(err => {
                    console.warn("Auto-play blocked, waiting for interaction:", err);
                });
            };

            const destroyHls = () => {
                if (hls) {
                    hls.destroy();
                    hls = null;
                }
            };

            const loadHlsSource = () => {
                if (!hlsSource) return false;
                destroyHls();
                if (canNativeHls) {
                    video.src = hlsSource;
                    video.load();
                    safePlay();
                    return true;
                } else if (Hls.isSupported()) {
                    hls = new Hls({
                        startLevel: -1,
                        capLevelToPlayerSize: false,
                        testBandwidth: false,
                    });
                    hls.loadSource(hlsSource);
                    hls.attachMedia(video);
                    hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
                        const highestLevel = Math.max(0, (data.levels?.length || 1) - 1);
                        if (hls) {
                            hls.startLevel = highestLevel;
                            hls.currentLevel = highestLevel;
                            hls.loadLevel = highestLevel;
                            hls.nextLevel = highestLevel;
                        }
                        safePlay();
                    });
                    hls.on(Hls.Events.ERROR, (_event, data) => {
                        if (data.fatal) {
                            triggerComplete();
                        }
                    });
                    return true;
                }
                return false;
            };

            const loadHighQualitySource = () => {
                if (!highQualitySource) return false;
                destroyHls();
                video.src = highQualitySource;
                video.load();
                safePlay();
                return true;
            };

            const handleEnded = () => {
                triggerComplete();
            };

            const handleCanPlay = () => {
                setIsLoading(false);
            };

            const handleError = () => {
                if (!attemptedHlsFallback && hlsSource && highQualitySource) {
                    attemptedHlsFallback = true;
                    setIsLoading(true);
                    if (loadHlsSource()) return;
                }
                triggerComplete();
            };

            const handleTimeUpdate = () => {
                if (!video.duration || Number.isNaN(video.duration)) return;
                if (video.currentTime >= video.duration - 0.12) {
                    triggerComplete();
                }
            };

            video.addEventListener('ended', handleEnded);
            video.addEventListener('canplay', handleCanPlay);
            video.addEventListener('error', handleError);
            video.addEventListener('timeupdate', handleTimeUpdate);
            if (!loadHighQualitySource() && !loadHlsSource()) {
                triggerComplete();
            }
            const safetyTimer = window.setTimeout(() => {
                if (!completedRef.current && loadingRef.current) {
                    triggerComplete();
                }
            }, 8000);

            return () => {
                video.removeEventListener('ended', handleEnded);
                video.removeEventListener('canplay', handleCanPlay);
                video.removeEventListener('error', handleError);
                video.removeEventListener('timeupdate', handleTimeUpdate);
                clearTimeout(safetyTimer);
                destroyHls();
            };
        }
    }, [hlsSrc, fallbackSrc]);

    // Render the full-screen player at document.body.  Keeping it inside the
    // application layout allowed later fixed controls (notably the global
    // contact shortcut) to enter a separate stacking context and steal clicks
    // from Mute/Skip even though the video looked visually uppermost.
    return createPortal((
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
                >
                    {/* Loading Indicator */}
                    {isLoading && (
                        <div className="absolute z-20 flex flex-col items-center text-white/70">
                            <div className="w-8 h-8 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mb-2"></div>
                            <span className="text-xs tracking-widest uppercase">Loading Scene...</span>
                        </div>
                    )}

                    <video
                        ref={videoRef}
                        className="w-full h-full object-cover"
                        playsInline
                        autoPlay
                        preload="auto"
                        muted={isMuted} // Essential for autoplay
                    />

                    <div className="absolute bottom-8 right-8 flex gap-4 z-20">
                        {/* Unmute Toggle */}
                        <button
                            onClick={() => setIsMuted(!isMuted)}
                            className="text-white/70 hover:text-white bg-black/30 hover:bg-black/50 border border-white/30 w-10 h-10 rounded-full flex items-center justify-center transition-all backdrop-blur-md"
                        >
                            {isMuted ? "🔇" : "🔊"}
                        </button>

                        <button
                            onClick={() => {
                                videoRef.current?.pause();
                                setIsVisible(false);
                                setTimeout(onComplete, 500);
                            }}
                            className="text-white/70 hover:text-white text-xs uppercase tracking-widest border-b border-white/20 hover:border-white/80 pb-0.5 transition-all"
                        >
                            Skip
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    ), document.body);
};
